import { usePathname } from '@classroom-ai/ui/routes/hooks';
import { HeaderSwitcher } from '@classroom-ai/ui/components/header-switcher';

import { paths } from 'src/routes/paths';

import { useActiveClass, type StudentClass, defaultSchedulePeriod } from 'src/class/class-provider';

// ----------------------------------------------------------------------

/** "Algebra 1 · Period 3" — the class identity shown to the student. */
function classLabel(c: StudentClass): string {
  const period = defaultSchedulePeriod(c);
  return [c.class.course.name, c.class.academicPeriod?.name, period?.name]
    .filter(Boolean)
    .join(' · ');
}

function classSubtitle(c: StudentClass): string {
  return [c.class.course.subject, c.class.course.gradeLevel].filter(Boolean).join(' · ');
}

// ----------------------------------------------------------------------

/**
 * Active-class indicator + switcher for the student dashboard header. Disabled
 * while a tutoring session is running so the student finishes one class before
 * switching to another. Hidden on My Classes, which shows every class at once (#433).
 */
export function ClassSwitcher() {
  const pathname = usePathname();
  const { classes, activeClass, setActiveClassId, isLoading, sessionLocked } = useActiveClass();

  if (pathname === paths.dashboard.classes) return null;

  return (
    <HeaderSwitcher
      icon="solar:notebook-bold-duotone"
      items={classes}
      activeItem={activeClass}
      getId={(c) => c.class.id}
      getLabel={classLabel}
      getSubtitle={classSubtitle}
      onSelect={setActiveClassId}
      emptyText="No classes yet"
      isLoading={isLoading}
      disabled={sessionLocked}
      disabledTooltip="Finish or end your current session to switch classes."
    />
  );
}
